import { useTranslation } from "@redotech/redo-i18n";
import PackageIcon from "@redotech/redo-icons/package.svg";
import { CoverageProductIdsContext } from "@redotech/redo-merchant-app-common/coverage-product-ids";
import { Order } from "@redotech/redo-order-core-common/order";
import { ReturnType } from "@redotech/redo-return-core-common/return";
import {
  CoverageFeeProductMatchType,
  isCoverageFeeProduct,
} from "@redotech/redo-return-coverage-common/is-coverage-fee-product";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { AnimatedSkeletonBlock } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { getDateString } from "@redotech/util/date-format";
import { memo, useContext } from "react";
import * as styles from "./order-search-result-card.module.css";

function isRedoLineItem(
  lineItem: any,
  redoCoverageProductIds: ReadonlySet<string>,
): boolean {
  return isCoverageFeeProduct({
    product: {
      vendor: lineItem.vendor,
      sku: lineItem.sku,
      product_id: lineItem.product_id?.toString(),
    },
    redoCoverageProductIds,
    matchType: CoverageFeeProductMatchType.REDO_ONLY,
  });
}

function getOrderItemCount(
  order: Order,
  redoCoverageProductIds: ReadonlySet<string>,
): number {
  return order.shopify.line_items
    .filter((item) => !isRedoLineItem(item, redoCoverageProductIds))
    .reduce((sum, item) => sum + item.quantity, 0);
}

export const OrderSearchResultCard = memo(function OrderSearchResultCard({
  order,
  returnType,
  onClick,
  hasReturnableItems,
}: {
  order: Order;
  returnType: ReturnType;
  onClick: () => void;
  hasReturnableItems?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const redoCoverageProductIds = useContext(CoverageProductIdsContext);
  const itemCount = getOrderItemCount(order, redoCoverageProductIds);
  const formattedDate = getDateString(new Date(order.createdAt), {
    locale: i18n.language,
  });

  const isProtected =
    returnType === "claim" ? order.packageProtected : order.protected;
  const handleClick = () => {
    onClick();
  };

  const protectionLabel = isProtected ? t("Protected") : t("Not protected");
  const unavailableLabel = (() => {
    // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
    switch (returnType) {
      case "claim":
        return t("No items available for claim");
      case "warranty":
        return t("No items available for warranty claim");
      case "recycle":
        return t("No items available for recycle");
      case "withdrawal":
        return t("No items available for withdrawal");
      default:
        return t("No items available for return");
    }
  })();
  const itemCountLabel = t("{{count}} items", { count: itemCount });

  return (
    // design-allow: existing order search result is a full-row click target
    <div className={styles.orderSearchResultCard} onClick={handleClick}>
      <Flex align="center" dir="row" gap="md">
        <RedoIcon color="primary" Icon={PackageIcon} size={16} />
        <Flex dir="column" gap="xs" w="full">
          <Flex align="center" dir="row" gap="sm">
            <Text fontSize="sm" fontWeight="semibold">
              {order.shopify.name}
            </Text>
            <RedoBadge
              color={isProtected ? "purple" : "gray"}
              size="xs"
              text={protectionLabel}
            />
            {hasReturnableItems === false && (
              <RedoBadge color="error" size="xs" text={unavailableLabel} />
            )}
          </Flex>
          <Flex align="center" dir="row" gap="xs">
            <Text fontSize="xs" fontWeight="regular" textColor="secondary">
              {order.customer_name}
            </Text>
            <Text fontSize="xs" fontWeight="regular" textColor="secondary">
              •
            </Text>
            <Text fontSize="xs" fontWeight="regular" textColor="secondary">
              {formattedDate}
            </Text>
            <Text fontSize="xs" fontWeight="regular" textColor="secondary">
              •
            </Text>
            <Text fontSize="xs" fontWeight="regular" textColor="secondary">
              {itemCountLabel}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
});

export const OrderSearchResultSkeleton = memo(
  function OrderSearchResultSkeleton() {
    return (
      <div className={styles.skeletonCard}>
        <Flex align="center" dir="row" gap="md">
          <AnimatedSkeletonBlock height="40px" width="40px" />
          <Flex dir="column" gap="sm" w="full">
            <Flex align="center" dir="row" gap="sm">
              <AnimatedSkeletonBlock height="20px" width="80px" />
              <AnimatedSkeletonBlock height="24px" width="100px" />
            </Flex>
            <AnimatedSkeletonBlock height="16px" width="60%" />
          </Flex>
        </Flex>
      </div>
    );
  },
);
